import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useLocalization from '@/context/localization/useLocalization';

type Props = {
  /** Pre-defined query to prioritize
   * instead of the search params */
  predefined?: {
    zones?: string[];
    lang?: Language;
    category?: string[];
    countryCode?: string;
    /** This will not apply any location based filter.
     * Useful for search query
     */
    locationGlobal?: boolean;
  };
};

const useExploreQuery = (props: Props) => {
  const predefined = props?.predefined;
  const { locale } = useLocalization();
  const [searchParams] = useSearchParams();
  const page = searchParams.get('page');
  const perPage = 5;
  const categories = searchParams.getAll('category');

  const categoriesQuery = useMemo(() => {
    if (predefined?.category?.length) {
      return {
        OR: [{ category: { slug_in: predefined.category } }],
      };
    }

    if (categories.length) {
      return {
        OR: [{ category: { slug_in: categories } }],
      };
    }
    return {};
  }, [predefined?.category, categories]);

  const zones = useMemo(() => searchParams.getAll('zone') || [], [searchParams]);

  const zoneQuery = useMemo(() => {
    if (predefined?.zones?.length) {
      return {
        OR: [{ zone: { slug_in: predefined.zones } }],
      };
    }

    if (zones.length) {
      return {
        OR: [{ zone: { slug_in: zones } }],
      };
    }

    return {};
  }, [predefined?.zones, zones]);

  const sortParam = searchParams.get('sort') || 'newest';

  const sortQuery = useMemo(() => {
    switch (sortParam) {
      case 'newest':
        return 'sys_publishedAt_DESC';
      case 'oldest':
        return 'sys_publishedAt_ASC';
      case 'high_price':
        return 'startingPrice_DESC';
      case 'low_price':
        return 'startingPrice_ASC';
      default:
        return ['order_ASC', 'sys_publishedAt_DESC'];
    }
  }, [sortParam]);

  const search = searchParams.get('q');
  const searchQuery = useMemo(() => {
    if (search) {
      return {
        OR: [
          { title_contains: search },
          { description_contains: search },
          { category: { title_contains: search } },
          { cmsTags: { title_contains: search } },
          { location: { title_contains: search } },
          {
            seo: {
              OR: [
                { title_contains: search },
                { keywords_contains: search },
                { description_contains: search },
              ],
            },
          },
        ],
      };
    }
    return {};
  }, [search]);

  /**
   * If still detecting country, return null until country resolved
   * because country is required for the query
   */
  const query = useMemo(() => {
    return {
      lang: predefined?.lang || locale,
      limit: perPage,
      skip: (Number(page) - 1) * perPage,
      order: sortQuery,
      where: {
        AND: [
          { ...zoneQuery },
          { ...categoriesQuery },
          { ...searchQuery },
          { season: { slug: 'riyadh-season-2025' } },
        ],
      },
    };
  }, [predefined?.lang, locale, perPage, page, sortQuery, zoneQuery, categoriesQuery, searchQuery]);
  return {
    query,
    search: search,
    perPage,
    categories,
    zones,
    sortBy: sortParam,
  };
};

export default useExploreQuery;
